// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type WorkOrderId, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { EntityStatusPoint } from 'components/Mantine/EntityStatusPoint';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { TriggerOnVisible } from 'hooks/useOnScreen';

type Props = {
  workOrderId: WorkOrderId;
};

export const WorkOrderShipmentsSection = ({ workOrderId }: Props) => {
  const defaultOpts = useMemo(() => ({ defaultConfig: { params: { workOrderIds: [workOrderId] } } }), [workOrderId]);
  const {
    data: shipments,
    fetchNextPage,
    loading: loadingShipments,
    setDefaultOpts,
    entireCount,
  } = useEvolvePaginatedGet('shop/shipment', defaultOpts);
  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  return (
    <DetailSection
      title="Shipping Orders"
      noContentMessage="No shipping orders contain items from this work order."
      showNoContentMessage={entireCount === 0}
    >
      <Stack gap="xs">
        {shipments.map((shipment) => (
          <Flex key={shipment.shipmentId} align="center" justify="space-between" gap="xs">
            <EvolveLink to={`/shipping-orders/${shipment.shipmentId}`} from="Work Orders" style={{ flex: 1 }}>
              <Text fz="sm" lineClamp={1}>
                {shipment.identifier}: {shipment.name}
              </Text>
            </EvolveLink>
            <EntityStatusPoint
              customStatusEntityName="Shipment"
              stateName={shipment.shipmentStateName}
              statusId={shipment.shipmentStatusId}
            />
          </Flex>
        ))}
        <TriggerOnVisible onVisible={fetchNextPage} loading={loadingShipments} />
      </Stack>
    </DetailSection>
  );
};
